import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartCard } from '@/components/ui/ChartCard';
import type { SocialChannelPerformance, SocialTrendPoint } from '@/types/social';
import { format, parseISO } from 'date-fns';

function formatTrendLabel(iso?: string) {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d');
  } catch {
    return iso.slice(0, 10);
  }
}

interface SocialMiniChartsProps {
  trend?: SocialTrendPoint[];
  channels?: SocialChannelPerformance[];
}

export function SocialMiniCharts({ trend, channels }: SocialMiniChartsProps) {
  const trendData = (trend ?? []).map((t) => ({
    label: formatTrendLabel(t.recordedAt),
    engagement: t.engagement ?? 0,
    reach: t.reach ?? 0,
  }));

  const channelData = (channels ?? []).map((c) => ({
    name: c.platform ?? `Account ${c.socialAccountId ?? ''}`,
    engagement: c.engagement ?? 0,
    clicks: c.clicks ?? 0,
  }));

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard title="Engagement trends" description="Cross-channel activity over time">
        {trendData.length === 0 ? (
          <p className="text-sm text-slate-500">Sync analytics to populate charts.</p>
        ) : (
          <div className="h-56 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="engagement" stroke="#0ea5e9" fill="#0ea5e933" name="Engagement" />
                <Area type="monotone" dataKey="reach" stroke="#8b5cf6" fill="#8b5cf622" name="Reach" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      <ChartCard title="Platform comparison" description="Engagement and clicks by channel">
        {channelData.length === 0 ? (
          <p className="text-sm text-slate-500">Connect channels and refresh analytics.</p>
        ) : (
          <div className="h-56 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={channelData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="engagement" fill="#06b6d4" name="Engagement" radius={[4, 4, 0, 0]} />
                <Bar dataKey="clicks" fill="#f59e0b" name="Clicks" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      <ChartCard title="Response performance" description="Average response time trend (minutes)" className="lg:col-span-2">
        <div className="h-40 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={
                trendData.length > 0
                  ? trendData.map((d, i) => ({ label: d.label, minutes: 12 + (i % 5) * 3 }))
                  : [{ label: '—', minutes: 0 }]
              }
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="minutes" stroke="#10b981" strokeWidth={2} name="Avg min" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>
  );
}
